// src/new/components/monitor/TriageChat/expressBackend.ts

import { TriageContext } from './triageContext';
import { TriageBackend, HistoryMessage, SubagentState, authHeaders } from './triageBackend';

/** One part of an OpenCode message. `text` parts rebuild the plain message
 *  text; a `task` tool part links its owning turn to a subagent child session
 *  via `state.metadata.sessionId` (see `taskChildIds`). Other part types are
 *  ignored. */
interface HistoryPart {
  type: string;
  text?: string;
  tool?: string;
  name?: string;
  state?: { metadata?: { sessionId?: unknown } };
}

/** OpenCode's message shape is `{ info: { id, role, sessionID }, parts }` —
 *  NOT `{ role, parts }` — unlike veda-ai's flatter SessionMessage. */
interface OpenCodeMessage {
  info: {
    id: string;
    role: 'user' | 'assistant';
    sessionID: string;
  };
  parts: HistoryPart[];
}

/** One persisted subagent (child session) transcript from the history
 *  endpoint. Mirrors express-code's own `HistoryChild` (chat-pane.tsx). */
interface HistoryChild {
  session_id: string;
  parent_id?: string;
  agent?: string | null;
  title?: string | null;
  messages: OpenCodeMessage[];
}

interface ChatHistoryResponse {
  messages?: OpenCodeMessage[];
  children?: HistoryChild[];
}

/** Join an OpenCode message's text parts into a single string, dropping
 *  non-text parts (tool calls, etc). Shared by the top-level message loop and
 *  the per-child subagent loop below. */
function textFromParts(parts: HistoryPart[] | undefined): string {
  return (parts ?? [])
    .filter((p) => p.type === 'text' && typeof p.text === 'string')
    .map((p) => p.text as string)
    .join('');
}

/** Child session ids a message's `task` tool-call parts link to. OpenCode
 *  sets `state.metadata.sessionId` (= child id) on a task part — the same link
 *  express-code's own `childIdForTask` (chat-pane.tsx) reads — so each subagent
 *  is attributed to the ONE turn that spawned it. */
function taskChildIds(parts: HistoryPart[] | undefined): string[] {
  const ids: string[] = [];
  for (const p of parts ?? []) {
    if (p.type !== 'tool' || (p.tool !== 'task' && p.name !== 'task')) continue;
    const sid = p.state?.metadata?.sessionId;
    if (typeof sid === 'string' && sid) ids.push(sid);
  }
  return ids;
}

/** Build a `childSessionId -> SubagentState` map from persisted history
 *  children — mirrors express-code's own `buildSubagentsFromChildren`
 *  (chat-pane.tsx). History is always settled, so every child is
 *  `status: 'done'`. Kept as a flat map (not squashed into a flattened text
 *  bubble — that would lose the structure the live subagent-start/subagent-end
 *  path produces); `getHistory` then attributes each child to its owning turn
 *  via `taskChildIds`. */
function buildSubagentsFromChildren(
  children: HistoryChild[] | undefined,
): Record<string, SubagentState> {
  const out: Record<string, SubagentState> = {};
  for (const child of children ?? []) {
    if (!child.session_id) continue;
    let taskPrompt: string | null = null;
    let outputText = '';
    const childMessages = Array.isArray(child.messages) ? child.messages : [];
    for (const m of childMessages) {
      const text = textFromParts(m.parts);
      if (m.info?.role === 'user' && taskPrompt == null) taskPrompt = text || null;
      else if (m.info?.role === 'assistant') outputText += text;
    }
    out[child.session_id] = {
      childSessionId: child.session_id,
      agent: child.agent ?? null,
      title: child.title ?? null,
      taskPrompt,
      outputText,
      status: 'done',
    };
  }
  return out;
}

/** Attribute each child session to the assistant turn whose `task` tool-call
 *  links to it (metadata.sessionId), so it renders under that one turn — NOT
 *  fanned out under every assistant bubble (the multi-turn reload duplication
 *  bug). TriageChat's AssistantBubble renders `Object.values(m.subagents)`,
 *  so a per-turn map is what keeps each subagent card to its owning turn. */
function attributeChildrenToTurns(
  rawMessages: OpenCodeMessage[],
  subagents: Record<string, SubagentState>,
): { messages: HistoryMessage[]; referenced: Set<string>; lastAssistantIdx: number } {
  const referenced = new Set<string>();
  const messages: HistoryMessage[] = [];
  let lastAssistantIdx = -1;
  for (const msg of rawMessages) {
    const role = msg.info?.role === 'user' ? 'user' : 'assistant';
    const text = textFromParts(msg.parts);
    const childIds = role === 'assistant' ? taskChildIds(msg.parts) : [];
    // Keep an assistant turn that owns subagents even if it has no text of
    // its own (a pure task-dispatch turn); otherwise skip empty messages.
    if (!text && childIds.length === 0) continue;
    if (role === 'assistant') {
      const own: Record<string, SubagentState> = {};
      for (const cid of childIds) {
        if (subagents[cid]) {
          own[cid] = subagents[cid];
          referenced.add(cid);
        }
      }
      messages.push(Object.keys(own).length > 0 ? { role, text, subagents: own } : { role, text });
      lastAssistantIdx = messages.length - 1;
    } else {
      messages.push({ role, text });
    }
  }
  return { messages, referenced, lastAssistantIdx };
}

/** Children whose linking `task` part couldn't be found (metadata absent)
 *  attach to the last assistant turn, so they still render exactly once
 *  rather than being dropped. */
function attachOrphanSubagents(
  messages: HistoryMessage[],
  subagents: Record<string, SubagentState>,
  referenced: Set<string>,
  lastAssistantIdx: number,
): HistoryMessage[] {
  const orphans = Object.keys(subagents).filter((cid) => !referenced.has(cid));
  if (orphans.length === 0 || lastAssistantIdx < 0) return messages;
  const target = messages[lastAssistantIdx];
  const merged = { ...(target.subagents ?? {}) };
  for (const cid of orphans) merged[cid] = subagents[cid];
  messages[lastAssistantIdx] = { ...target, subagents: merged };
  return messages;
}

/**
 * Build the express-code `TriageBackend` implementation. `baseUrl`/`token`
 * are captured the same way `vedaSession.ts`'s functions take them — as
 * plain params — rather than inventing a new auth mechanism; `TriageBackend`
 * methods don't take a token per call, so this factory closes over them.
 */
export function createExpressBackend(baseUrl: string, token: string): TriageBackend {
  return {
    kind: 'express',

    // Distinct from veda-ai's `triage.session` prefix (see vedaBackend.ts) so
    // a cached veda-ai session UUID is never passed to express-code's history
    // endpoint, which would 404 (R1 in the migration plan).
    storageKeyPrefix: 'triage.express.session',

    async createSession(ctx: TriageContext): Promise<{ sessionId: string }> {
      const res = await fetch(`${baseUrl}/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body: JSON.stringify({
          mode_id: 'triage',
          title: `Triage: ${ctx.resourceName ?? ctx.resourceType} #${ctx.resourceId}`,
          source: 'nexla-ui-triage',
        }),
      });
      if (!res.ok) {
        throw new Error(`express-code session create failed: ${res.status}`);
      }
      const data = await res.json();
      if (!data.id) {
        throw new Error('express-code session create returned no id');
      }
      return { sessionId: data.id };
    },

    async getHistory(sessionId: string): Promise<HistoryMessage[]> {
      const res = await fetch(`${baseUrl}/chat/${sessionId}/history`, {
        headers: authHeaders(token),
      });
      if (!res.ok) {
        throw new Error(`express-code get history failed: ${res.status}`);
      }
      const data = (await res.json()) as ChatHistoryResponse;

      const subagents = buildSubagentsFromChildren(data.children);
      const { messages, referenced, lastAssistantIdx } = attributeChildrenToTurns(
        Array.isArray(data.messages) ? data.messages : [],
        subagents,
      );
      return attachOrphanSubagents(messages, subagents, referenced, lastAssistantIdx);
    },

    async abortSession(sessionId: string): Promise<void> {
      const res = await fetch(`${baseUrl}/chat/${sessionId}/abort`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      if (!res.ok) {
        throw new Error(`express-code session abort failed: ${res.status}`);
      }
    },
  };
}
